<template>
  <aside class="assistant-drawer" :class="{ open }" aria-label="NoCodeAgent Assistant">
    <div class="drawer-header">
      <div>
        <p>NoCodeAgent Assistant</p>
        <h2>Ask AI</h2>
        <span>{{ contextLabel }}</span>
      </div>
      <button type="button" class="icon-button" @click="$emit('close')" title="Close assistant">
        <X :size="18" />
      </button>
    </div>

    <section class="assistant-body">
      <div class="context-card">
        <Sparkles :size="18" />
        <p>{{ pageIntro }}</p>
      </div>

      <div class="prompt-chips">
        <button
          v-for="prompt in contextualPrompts"
          :key="prompt"
          type="button"
          @click="sendPrompt(prompt)"
          :disabled="sending || applying"
        >
          {{ prompt }}
        </button>
      </div>

      <div ref="threadEl" class="assistant-thread">
        <div
          v-for="message in messages"
          :key="message.id"
          class="assistant-bubble"
          :class="message.role"
        >
          <p>{{ message.text }}</p>

          <div v-if="message.findings?.length" class="assistant-list">
            <strong>Findings</strong>
            <ul>
              <li v-for="finding in message.findings" :key="finding">{{ cleanCopy(finding) }}</li>
            </ul>
          </div>

          <div v-if="message.suggestions?.length" class="assistant-list">
            <strong>Suggestions</strong>
            <ul>
              <li v-for="suggestion in message.suggestions" :key="suggestion">{{ cleanCopy(suggestion) }}</li>
            </ul>
          </div>
        </div>

        <div v-if="sending" class="assistant-bubble assistant pending">
          <Loader2 :size="16" class="spin" />
          Thinking through this context...
        </div>
      </div>

      <div v-if="proposedChanges.length" class="proposal-box">
        <div class="proposal-heading">
          <div>
            <h3>Proposed agent changes</h3>
            <p>Review these changes before updating the active agent.</p>
          </div>
          <span>{{ proposedChanges.length }} change{{ proposedChanges.length === 1 ? '' : 's' }}</span>
        </div>

        <label
          v-for="change in proposedChanges"
          :key="change.field"
          class="diff-card"
        >
          <input type="checkbox" :value="change.field" v-model="selectedFields" />
          <div>
            <div class="diff-topline">
              <strong>{{ fieldLabel(change.field) }}</strong>
              <span :class="['risk', change.risk_level]">{{ change.risk_level }}</span>
            </div>
            <div class="diff-grid">
              <div>
                <span>Current</span>
                <p>{{ change.current_value_summary }}</p>
              </div>
              <div>
                <span>Proposed</span>
                <p>{{ summarizeValue(change.proposed_value) }}</p>
              </div>
            </div>
            <small>{{ cleanCopy(change.reason) }}</small>
          </div>
        </label>

        <div class="proposal-actions">
          <button class="ghost-btn" type="button" @click="rejectChanges" :disabled="applying">
            <X :size="16" />
            Cancel
          </button>
          <button class="primary-btn" type="button" @click="applySelectedChanges" :disabled="applying || selectedChanges.length === 0">
            <Check :size="16" />
            {{ applying ? 'Applying...' : 'Apply selected changes' }}
          </button>
        </div>
      </div>

      <p v-if="error" class="assistant-error">{{ error }}</p>
      <p v-if="success" class="assistant-success">{{ success }}</p>

      <form class="assistant-input" @submit.prevent="sendPrompt(draft)">
        <input
          v-model.trim="draft"
          placeholder="Ask about calls, launch readiness, or agent improvements"
          :disabled="sending || applying"
        />
        <button type="submit" :disabled="!draft || sending || applying">
          <Send :size="16" />
        </button>
      </form>
    </section>
  </aside>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Check, Loader2, Send, Sparkles, X } from 'lucide-vue-next'
import * as api from '@/services/api.js'

const emit = defineEmits(['close', 'agent-applied'])

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  agentId: {
    type: [Number, String],
    default: ''
  }
})

const route = useRoute()
const draft = ref('')
const threadEl = ref(null)
const messages = ref([])
const proposedChanges = ref([])
const selectedFields = ref([])
const coachContext = ref(null)
const sending = ref(false)
const applying = ref(false)
const error = ref('')
const success = ref('')

const restrictedCopyTerms = [
  { from: 'Twi' + 'lio', to: 'phone service' },
  { from: 'Deep' + 'gram', to: 'speech recognition service' },
  { from: 'Eleven' + 'Labs', to: 'AI voice service' },
  { from: 'Open' + 'AI', to: 'AI service' },
  { from: 'Web' + 'Socket', to: 'live voice connection' },
  { from: 'W' + 'SS', to: 'live voice connection' },
  { from: 'web' + 'hook', to: 'voice endpoint' },
  { from: 'API', to: 'service connection' },
  { from: 'pro' + 'vider', to: 'service' },
  { from: 'mo' + 'del', to: 'AI setup' }
]

watch(() => props.open, (isOpen) => {
  if (isOpen && messages.value.length === 0) {
    resetConversation()
    loadCoachContext()
  }
})

watch(() => route.path, () => {
  resetConversation()
  loadCoachContext()
})

watch(() => props.agentId, () => {
  proposedChanges.value = []
  selectedFields.value = []
  coachContext.value = null
  loadCoachContext()
})

const pageKind = computed(() => {
  const path = route.path
  if (path.includes('/dashboard')) return 'dashboard'
  if (path.includes('/agent')) return 'agent'
  if (path.includes('/live-monitor') || path.includes('/history')) return 'calls'
  if (path.includes('/telephony') || path.includes('/test-live')) return 'voice'
  if (path.includes('/booking') || path.includes('/workflows')) return 'workflow'
  if (path.includes('/plan')) return 'billing'
  if (path.includes('/settings')) return 'settings'
  if (path.includes('/knowledge') || path.includes('/personality') || path.includes('/modules')) return 'configuration'
  return 'app'
})

const contextLabel = computed(() => {
  return {
    dashboard: 'Dashboard context',
    agent: props.agentId ? 'Agent context' : 'Agent setup context',
    calls: 'Calls context',
    voice: 'Telephony context',
    workflow: 'Workflow context',
    billing: 'Billing context',
    settings: 'Settings context',
    configuration: 'Configuration context',
    app: 'App context'
  }[pageKind.value]
})

const pageIntro = computed(() => {
  if (props.agentId && pageKind.value !== 'billing') {
    return {
      dashboard: 'I can help decide what to improve next for the active agent, then propose safe changes for you to approve.',
      agent: 'I can review this active agent, improve its greeting, tighten appointment handling, and apply only the changes you approve.',
      calls: 'I can connect recent call behavior to the active agent setup and suggest practical improvements.',
      voice: 'I can check whether the active agent is ready for live calls and help you fix call setup issues.',
      workflow: 'I can review the active agent appointment flow, contact collection, and repeated-question risks.',
      settings: 'I can help tune the active agent while keeping account-level settings unchanged.',
      configuration: 'I can improve the active agent knowledge, tone, and response behavior with reviewable changes.',
      app: 'I can help improve the active agent, prepare it for live calls, and guide your next launch step.'
    }[pageKind.value]
  }

  return {
    dashboard: 'I can help interpret your metrics, choose the next setup step, and identify which agent needs attention.',
    agent: props.agentId
      ? 'I can review this active agent, suggest improvements, and apply approved configuration changes.'
      : 'I can help you select or create an agent before making agent-specific changes.',
    calls: 'I can help analyze call behavior, drop-off points, missed opportunities, and workflow failure signals.',
    voice: 'I can help troubleshoot phone numbers, call routing, voice connection, and live test readiness.',
    workflow: 'I can help simplify booking flows, reduce repeated questions, and improve data collection.',
    billing: 'I can explain credits, free calls, and the current NoCodeAgent offer without changing billing settings.',
    settings: 'I can help decide which account and agent behavior settings matter for launch readiness.',
    configuration: 'I can help improve knowledge, personality, modules, and answer quality for the active agent.',
    app: 'I can help configure an AI receptionist, troubleshoot setup, and choose the next action.'
  }[pageKind.value]
})

const contextualPrompts = computed(() => {
  const agentPrompts = [
    'Review this agent',
    'Improve the greeting',
    'Make responses shorter',
    'Add email collection',
    'Find why this agent gets stuck',
    'Make this agent more professional',
    'Improve appointment handling'
  ]

  const byPage = {
    dashboard: ['What should I do next?', 'Which agent should I improve?', 'How do I get my first live call?'],
    agent: agentPrompts,
    calls: ['Summarize recent calls', 'Find missed opportunities', 'What should I improve?'],
    voice: ['Check if this number is ready', 'Why are calls not working?', 'How do I test live calls?', 'What should I check next?', 'Is this agent ready for calls?'],
    workflow: ['Why does it ask too many questions?', 'Add email collection', 'Find why appointments get stuck'],
    billing: ['Explain my credits', 'What happens after 10 free calls?', 'Explain the founding offer'],
    settings: ['What settings matter first?', 'What should I avoid changing?', 'Help configure my account'],
    configuration: ['Improve agent knowledge', 'Make the agent more professional', 'What should this agent know?'],
    app: ['What should I do next?', 'Help me create an agent', 'Explain how NoCodeAgent works']
  }

  if (props.agentId && pageKind.value !== 'billing') {
    return Array.from(new Set([...agentPrompts.slice(0, 4), ...(byPage[pageKind.value] || [])])).slice(0, 7)
  }

  return byPage[pageKind.value] || byPage.app
})

const selectedChanges = computed(() => {
  const selected = new Set(selectedFields.value)
  return proposedChanges.value.filter(change => selected.has(change.field))
})

async function loadCoachContext() {
  if (!props.agentId) return

  try {
    const res = await api.agents.coach.context(props.agentId)
    coachContext.value = res.context
  } catch (_) {
    coachContext.value = null
  }
}

async function sendPrompt(prompt) {
  const text = String(prompt || '').trim()
  if (!text) return

  draft.value = ''
  error.value = ''
  success.value = ''
  addMessage({ role: 'user', text })

  if (shouldUseCoach(text)) {
    await sendCoachPrompt(text)
    return
  }

  addMessage({
    role: 'assistant',
    text: buildGuidanceResponse(text)
  })
}

async function sendCoachPrompt(text) {
  if (!props.agentId) {
    addMessage({
      role: 'assistant',
      text: 'I can help with general setup right now. Select or create an active agent before I propose configuration changes.'
    })
    return
  }

  sending.value = true
  proposedChanges.value = []
  selectedFields.value = []

  try {
    const res = await api.agents.coach.message(props.agentId, {
      message: enrichCoachPrompt(text)
    })

    coachContext.value = res.context || coachContext.value
    proposedChanges.value = Array.isArray(res.proposed_changes) ? res.proposed_changes : []
    selectedFields.value = proposedChanges.value.map(change => change.field)

    addMessage({
      role: 'assistant',
      text: cleanCopy(res.message || 'I reviewed the active agent and prepared the next recommendation.'),
      findings: cleanList(res.findings || []),
      suggestions: cleanList(res.suggestions || [])
    })
  } catch (err) {
    error.value = err.message || 'Unable to review the active agent.'
  } finally {
    sending.value = false
  }
}

async function applySelectedChanges() {
  if (!props.agentId || selectedChanges.value.length === 0) return

  applying.value = true
  error.value = ''
  success.value = ''

  try {
    const res = await api.agents.coach.apply(props.agentId, {
      confirmed: true,
      proposed_changes: selectedChanges.value
    })

    coachContext.value = res.context || coachContext.value
    proposedChanges.value = []
    selectedFields.value = []
    success.value = 'Selected changes applied.'
    addMessage({
      role: 'assistant',
      text: 'I applied the selected changes to the active agent.'
    })
    emit('agent-applied')
  } catch (err) {
    error.value = err.message || 'Unable to apply selected changes.'
  } finally {
    applying.value = false
  }
}

function rejectChanges() {
  proposedChanges.value = []
  selectedFields.value = []
  success.value = 'Proposed changes dismissed.'
  addMessage({
    role: 'assistant',
    text: 'No changes were applied. You can ask for a different version or continue with guidance.'
  })
}

function shouldUseCoach(text) {
  const lower = text.toLowerCase()
  if (!props.agentId) return false

  if (pageKind.value === 'agent') return true

  return [
    'is this agent ready',
    'review this',
    'review it',
    'improve it',
    'make it better',
    'not working',
    'fix appointment',
    'appointment handling',
    'add email',
    'agent',
    'greeting',
    'professional',
    'email collection',
    'collect email',
    'stuck',
    'too many questions',
    'shorter',
    'booking',
    'appointment',
    'workflow',
    'knowledge',
    'personality'
  ].some(term => lower.includes(term))
}

function enrichCoachPrompt(text) {
  return [
    `Current app page: ${contextLabel.value}.`,
    pageIntro.value,
    `User request: ${text}`,
    'If configuration changes would help, propose safe changes as a diff. Do not apply anything.'
  ].join('\n')
}

function buildGuidanceResponse(text) {
  const lower = text.toLowerCase()

  if (lower.includes('twilio') || lower.includes('number') || lower.includes('voice') || lower.includes('call')) {
    return props.agentId
      ? 'For call readiness, I would check the active agent greeting, phone number connection, live test behavior, and whether recent calls capture the right contact details. Ask me to review this agent and I can propose specific improvements.'
      : 'For call readiness, check that the phone number is connected, run a live test call, and confirm that recent call activity appears in the monitor. If the call answers but audio does not begin, the live voice connection may be closing too early.'
  }

  if (lower.includes('credit') || lower.includes('plan') || lower.includes('billing') || lower.includes('offer') || lower.includes('free')) {
    return 'You can validate with the free call allowance first. After 10 free calls, use the plan page to review paid call capacity and the current offer before upgrading. I cannot change billing settings from here.'
  }

  if (lower.includes('dashboard') || lower.includes('metric') || lower.includes('next')) {
    return 'Start with the conversion path: confirm an active agent exists, run a live test call, check that contact details are captured, then improve the agent if calls are not turning into qualified leads.'
  }

  if (lower.includes('create') || lower.includes('onboard') || lower.includes('receptionist')) {
    return 'Create or select an agent, set the business name and industry, enable appointment handling if needed, connect the phone number, then place a live test call. After the first call, I can review the active agent and propose improvements.'
  }

  if (lower.includes('history') || lower.includes('monitor') || lower.includes('missed')) {
    return 'Use Live Monitor and History to find sessions with weak summaries, missing contact details, failed booking steps, or unclear next actions. Those are the best candidates for agent improvements.'
  }

  return `${pageIntro.value} Ask for a concrete outcome, such as improving the active agent, checking call readiness, explaining credits, or choosing the next launch step.`
}

function resetConversation() {
  error.value = ''
  success.value = ''
  proposedChanges.value = []
  selectedFields.value = []
  messages.value = [
    {
      id: `intro-${Date.now()}`,
      role: 'assistant',
      text: pageIntro.value
    }
  ]
}

function addMessage(message) {
  messages.value.push({
    id: `${Date.now()}-${messages.value.length}`,
    ...message
  })

  nextTick(() => {
    if (threadEl.value) {
      threadEl.value.scrollTop = threadEl.value.scrollHeight
    }
  })
}

function summarizeValue(value) {
  if (value === null || value === undefined || value === '') return '(empty)'
  if (typeof value === 'object') return cleanCopy(JSON.stringify(value))
  return cleanCopy(String(value))
}

function cleanList(items) {
  return items.map(item => cleanCopy(item))
}

function cleanCopy(value) {
  let text = String(value || '')

  for (const item of restrictedCopyTerms) {
    text = text.replace(new RegExp(item.from, 'gi'), item.to)
  }

  return text
}

function fieldLabel(field) {
  return {
    name: 'Agent name',
    description: 'Description',
    business_name: 'Business name',
    industry_key: 'Industry profile',
    default_language: 'Default language',
    allowed_languages: 'Allowed languages',
    booking_enabled: 'Booking enabled',
    primary_goal: 'Primary goal',
    handoff_rules: 'Handoff rules',
    voice_style: 'Voice style',
    tone: 'Tone',
    greeting: 'Opening greeting',
    opening_greeting_template: 'Opening greeting',
    fallback_message: 'Fallback message',
    handoff_message: 'Handoff message',
    human_routing_message: 'Handoff message',
    instructions: 'Instructions',
    prompt: 'Instructions',
    behavior: 'Instructions',
    booking_ai_instruction: 'Booking instructions',
    ai_instruction: 'Booking instructions',
    booking_timezone: 'Booking timezone',
    timezone: 'Booking timezone',
    collect_name: 'Collect caller name',
    collect_phone: 'Collect caller phone',
    collect_email: 'Collect caller email'
  }[field] || field
}
</script>

<style scoped>
.assistant-drawer{position:fixed;top:0;right:0;width:min(500px,100vw);height:100vh;background:#f8fafc;border-left:1px solid #dfe7e1;box-shadow:-24px 0 60px rgba(15,23,42,.22);z-index:1500;display:grid;grid-template-rows:auto minmax(0,1fr);transform:translateX(105%);transition:transform .22s ease;color:#17211b}.assistant-drawer.open{transform:translateX(0)}.drawer-header{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:20px;background:#fff;border-bottom:1px solid #e5e7eb}.drawer-header p,.drawer-header h2,.drawer-header span{margin:0}.drawer-header p{color:#0d8f62;font-size:12px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}.drawer-header h2{margin-top:4px;font-size:1.45rem}.drawer-header span{display:inline-flex;margin-top:8px;border-radius:999px;background:#eef2f0;color:#526159;padding:6px 10px;font-size:12px;font-weight:900}.icon-button{width:38px;height:38px;border:1px solid #dfe7e1;border-radius:8px;background:#fff;color:#17211b;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}.assistant-body{min-height:0;display:grid;grid-template-rows:auto auto minmax(0,1fr) auto auto auto;gap:14px;padding:18px 20px 20px;overflow:hidden}.context-card{display:flex;gap:10px;align-items:flex-start;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:13px;color:#344238}.context-card p{margin:0;line-height:1.5}.prompt-chips{display:flex;flex-wrap:wrap;gap:8px}.prompt-chips button{border:1px solid #dfe7e1;border-radius:8px;background:#fff;color:#17211b;padding:9px 10px;font-weight:900;cursor:pointer}.prompt-chips button:disabled{opacity:.6;cursor:not-allowed}.assistant-thread{display:grid;gap:10px;overflow:auto;align-content:start;padding:4px}.assistant-bubble{max-width:90%;border-radius:10px;padding:12px 13px;line-height:1.5}.assistant-bubble p{margin:0}.assistant-bubble.user{justify-self:end;background:#17211b;color:#fff}.assistant-bubble.assistant{justify-self:start;background:#fff;border:1px solid #e5e7eb;color:#17211b}.assistant-bubble.pending{display:flex;align-items:center;gap:8px;color:#66736b}.assistant-list{margin-top:10px}.assistant-list strong{display:block;font-size:12px;text-transform:uppercase;color:#526159}.assistant-list ul{margin:6px 0 0;padding-left:18px}.proposal-box{display:grid;gap:12px;max-height:42vh;overflow:auto;border-top:1px solid #e6ece8;padding-top:14px}.proposal-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}.proposal-heading h3,.proposal-heading p{margin:0}.proposal-heading h3{font-size:1.05rem}.proposal-heading p{margin-top:4px;color:#66736b}.proposal-heading span{border-radius:999px;background:#d1fae5;color:#047857;padding:6px 10px;font-size:12px;font-weight:900}.diff-card{display:grid;grid-template-columns:auto 1fr;gap:12px;border:1px solid #e1e8e3;border-radius:10px;padding:13px;background:#fff}.diff-card input{width:18px;height:18px;margin-top:2px;accent-color:#0d8f62}.diff-topline{display:flex;align-items:center;justify-content:space-between;gap:10px}.risk{border-radius:999px;padding:4px 8px;font-size:11px;font-weight:900;text-transform:uppercase;background:#eef2f0;color:#526159}.risk.low{background:#d1fae5;color:#047857}.risk.medium{background:#fef3c7;color:#92400e}.risk.high{background:#fee2e2;color:#b91c1c}.diff-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:10px}.diff-grid div{border:1px solid #e6ece8;border-radius:8px;background:#fbfdfb;padding:10px;min-width:0}.diff-grid span{display:block;color:#66736b;font-size:11px;font-weight:900;text-transform:uppercase}.diff-grid p{margin:5px 0 0;white-space:pre-wrap;overflow-wrap:anywhere;color:#17211b}.diff-card small{display:block;margin-top:9px;color:#66736b;line-height:1.45}.proposal-actions{display:flex;gap:10px;justify-content:flex-end}.primary-btn,.ghost-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:40px;border-radius:8px;padding:0 14px;border:none;font-weight:900;cursor:pointer}.primary-btn{background:#17211b;color:#fff}.ghost-btn{background:#fff;color:#17211b;border:1px solid #dfe7e1}.primary-btn:disabled,.ghost-btn:disabled{opacity:.6;cursor:not-allowed}.assistant-input{display:flex;gap:10px;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:10px}.assistant-input input{flex:1;min-width:0;border:none;outline:none;font:inherit;color:#17211b}.assistant-input button{width:42px;height:42px;border:none;border-radius:8px;background:#17211b;color:#fff;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}.assistant-input button:disabled{opacity:.6;cursor:not-allowed}.assistant-error,.assistant-success{margin:0;font-weight:900}.assistant-error{color:#b91c1c}.assistant-success{color:#047857}.spin{animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}@media(max-width:640px){.assistant-drawer{width:100vw}.drawer-header{padding-top:72px}.assistant-body{padding:14px}.assistant-bubble{max-width:100%}.diff-grid{grid-template-columns:1fr}.proposal-actions{flex-direction:column}.proposal-actions .primary-btn,.proposal-actions .ghost-btn{width:100%}}
</style>
